import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ListPresencesResponse,
  Presence,
  PresenceCheckInInput,
  PresenceEquipmentAction,
} from 'shared';
import { api } from '@/lib/api';

export type { Presence };

export const presenceKeys = {
  all: ['presences'] as const,
  list: (centerId: number) => [...presenceKeys.all, 'list', centerId] as const,
};

/** How often the list of who is in the centre is read again. */
export const PRESENCES_REFRESH_MS = 10_000;

/**
 * Who is in the centre right now, with the equipment they hold. Read again
 * every few seconds: participants also check in and scan on their own phones.
 */
export function usePresences(centerId: number | null) {
  return useQuery({
    queryKey: presenceKeys.list(centerId!),
    queryFn: () => api.get<ListPresencesResponse>(`/api/presences?digitalCenterId=${centerId}`),
    enabled: centerId !== null,
    refetchInterval: PRESENCES_REFRESH_MS,
  });
}

/** Everything a front desk action can change is read again after it. */
function useRefresh() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: presenceKeys.all });
    qc.invalidateQueries({ queryKey: ['equipments'] });
  };
}

/** Check a participant into a centre for them. */
export function useCheckInParticipant() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (input: PresenceCheckInInput) => api.post<Presence>('/api/presences', input),
    // Settled, not only on success: a refusal often means the list is stale.
    onSettled: refresh,
  });
}

/** End a participant's visit for them. */
export function useCheckOutParticipant() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (participantId: number) =>
      api.post<void>(`/api/presences/${participantId}/check-out`),
    onSettled: refresh,
  });
}

/** Hand a participant a piece of equipment, or take it back. */
export function usePresenceEquipment() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: ({
      participantId,
      equipmentId,
      action,
    }: {
      participantId: number;
      equipmentId: number;
      action: PresenceEquipmentAction;
    }) => api.post<unknown>(`/api/presences/${participantId}/equipment`, { equipmentId, action }),
    onSettled: refresh,
  });
}
